<script>
    import { onMount, untrack } from 'svelte';
    import pkg from 'mapbox-gl';
    import 'mapbox-gl/dist/mapbox-gl.css';
    import allData from './data/all_above_average_snowfall_frequency.json';
    import strongData from './data/mod_strong_above_average_snowfall_frequency.json'
    const { Map, NavigationControl } = pkg;

    // console.log([... new Set(allData.features.map(d => d.properties.band_max))]);

    // `data` is wired through from App.svelte (loaded from src/data/data.json)
    let { width = 600, data = [] } = $props();

    const height = 250;

    let allNinosEl = $state(null);
    let strongModNinosEl = $state(null);

    let allMap = $state();
    let strongMap = $state();

    // Initial map framing, switched at the 660/661px breakpoint.
    // 661px & above (desktop) vs 660px & below (mobile).
    const views = {
        desktop: {
            center: [-97.39117015535506, 45.479021790331316],
            zoom: 2.369746065200719,
        },
        mobile: {
            center: [-95.0513648897363, 46.38470520636952],
            zoom: 1.8461738840424975,
        },
    };



    // Single source of truth for the diverging color scale. Keyed by
    // `band_label` (always present and unique) so the map fills and the legend
    // can never drift out of sync. `max` is the upper edge of each bin, used
    // for the legend tick labels (null for the open-ended "Above 50" bin).
    const bands = [
        { label: '0 to 5', color: '#c99400', max: 5 },
        { label: '5 to 15', color: '#d6a645', max: 15 },
        { label: '15 to 25', color: '#e0b870', max: 25 },
        { label: '25 to 35', color: '#e8ca9b', max: 35 },
        { label: '35 to 45', color: '#eeddc5', max: 45 },
        { label: '45 to 55', color: '#f1f1f1', max: 55 },
        { label: '55 to 65', color: '#cacbd2', max: 65 },
        { label: '65 to 75', color: '#a4a7b5', max: 75 },
        { label: '75 to 85', color: '#7f8498', max: 85 },
        { label: '85 to 95', color: '#5c637c', max: 95 },
        { label: '95 to 100', color: '#394461', max: 100 },
    ];

    // `match` on band_label -> fill color, shared by both maps.
    const fillColor = [
        'match',
        ['get', 'band_label'],
        ...bands.flatMap((b) => [b.label, b.color]),
        '#ffffff', // fallback (should never be hit)
    ];

    // Adds the snowfall fill layer, then the state/province borders on top.
    // Borders are added last so they always render ABOVE the data fills.
    function addSnowLayers(map, data, id) {
        map.addSource(id, { type: 'geojson', data });
        map.addLayer({
            id,
            source: id,
            type: 'fill',
            paint: { 'fill-color': fillColor },
        }, 'bridge-rail-tracks');

        if (!map.getSource('admin-boundaries')) {
            map.addSource('admin-boundaries', {
                type: 'vector',
                url: 'mapbox://mapbox.mapbox-streets-v8',
            });
        }
        map.addLayer({
            id: 'state-borders',
            type: 'line',
            source: 'admin-boundaries',
            'source-layer': 'admin',
            filter: [
                'all',
                ['==', ['get', 'admin_level'], 1],
                ['==', ['get', 'maritime'], 'false'],
            ],
            paint: {
                'line-color': '#666666',
                'line-width': 0.6,
                'line-opacity': 0.4,
            },
        });

        // Country borders (admin_level 0) sit on top of the state borders so
        // national boundaries read as the stronger line.
        map.addLayer({
            id: 'country-borders',
            type: 'line',
            source: 'admin-boundaries',
            'source-layer': 'admin',
            filter: [
                'all',
                ['==', ['get', 'admin_level'], 0],
                ['==', ['get', 'maritime'], 'false'],
            ],
            paint: {
                'line-color': '#444444',
                'line-width': 1,
                'line-opacity': 0.6,
            },
        });
    }

    // Keep both maps framed for the current breakpoint. This runs on mount and
    // again whenever the pym-driven `width` prop updates (e.g. once the parent
    // reports the real container width, or the embed is resized across 660px).
    // Reading `width` here — rather than once inside onMount — is what makes the
    // desktop view reachable: child components mount before the parent sets the
    // real width, so at onMount time `width` is still the default 600.
    $effect(() => {
        const view = width >= 661 ? views.desktop : views.mobile;
        [allMap, strongMap].forEach((map) => map?.jumpTo(view));
    });

    onMount(() => {
        // Initial framing for the first paint; the $effect above keeps it in
        // sync with the real width once the parent reports it.
        const view = width >= 661 ? views.desktop : views.mobile;
        // console.log(view);

        if (allNinosEl) {
            allMap = new Map({
                accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
                container: 'map-all-ninos',
                style: "mapbox://styles/cnndigital/cmvog3ijj00cl01qq0wfthke2",
                // Conic projections must be passed as an object to rotate them:
                // the projection's own `center` longitude is the central meridian
                // (what orients the cone over North America), and `parallels` are
                // the standard parallels. The top-level `center`/`zoom` below stay
                // the camera position.
                projection: {
                    name: 'lambertConformalConic',
                    // Northern-tuned for the US/Canada snowfall extent.
                    center: [-96, 45],
                    parallels: [40, 52],
                    // Previous (standard CONUS) values — swap back if preferred:
                    // center: [-96, 40],
                    // parallels: [33, 45],
                },
                center: view.center,
                zoom: view.zoom,
                maxZoom: 5.8,
                attributionControl: false,
                cooperativeGestures: true
            });

            // Zoom in/out buttons, but no scroll-wheel zoom (so the page can
            // still scroll past the embed without the map hijacking it).
            allMap.addControl(new NavigationControl({ showCompass: false }), 'top-right');
            allMap.dragPan.disable();
            

            allMap.on('load', () => {
                addSnowLayers(allMap, allData, 'all-data');
            });

          

            allMap.on('moveend', () => {
                // console.log('center: [' + allMap.getCenter().lng + ', ' + allMap.getCenter().lat + '],\n' + 'zoom: ' + allMap.getZoom() + ',');
            })
        }
        if (strongModNinosEl) {
            strongMap = new Map({
                accessToken: import.meta.env.VITE_MAPBOX_TOKEN,
                container: 'map-strong-mod',
                style: "mapbox://styles/cnndigital/cmvog3ijj00cl01qq0wfthke2",
                // Conic projections must be passed as an object to rotate them:
                // the projection's own `center` longitude is the central meridian
                // (what orients the cone over North America), and `parallels` are
                // the standard parallels. The top-level `center`/`zoom` below stay
                // the camera position.
                projection: {
                    name: 'lambertConformalConic',
                    // Northern-tuned for the US/Canada snowfall extent.
                    center: [-96, 45],
                    parallels: [40, 52],
                    // Previous (standard CONUS) values — swap back if preferred:
                    // center: [-96, 40],
                    // parallels: [33, 45],
                },
                center: view.center,
                zoom: view.zoom,
                maxZoom: 5.8,
                attributionControl: false,
                cooperativeGestures: true
            })

            strongMap.addControl(new NavigationControl({ showCompass: false }), 'top-right');
            strongMap.dragPan.disable();

            strongMap.on('load', () => {
                addSnowLayers(strongMap, strongData, 'strong-data');
            });
           
        }
    })

</script>

<div class="legend">
    <div class="legend__title">Percent of winters with above-average snowfall</div>
    <div class="legend__bar">
        {#each bands as band, i}
            <div class="legend__cell">
                {#if i === 0}
                    <span class="legend__edge legend__edge--start">0</span>
                {/if}
                <span class="legend__swatch" style="background-color:{band.color}"></span>
                {#if band.max !== null}
                    <span
                        class="legend__edge"
                        class:legend__edge--end={i === bands.length - 1}
                    >{band.max}</span>
                {/if}
            </div>
        {/each}
    </div>
    <div class="legend__ends">
        <span>&larr; Fewer winters</span>
        <span>More winters &rarr;</span>
    </div>
</div>

<div class="map-row">
    <div class="map-flex-child">
        <h3>El Niño winters</h3>
        <div class="map map-all-ninos" id="map-all-ninos" bind:this={allNinosEl}>

        </div>
    </div>
    <div class="map-flex-child">
        <h3>Moderate-to-strong El Niño winters</h3>
        <div class="map map-strong-mod" id="map-strong-mod" bind:this={strongModNinosEl}>

        </div>
    </div>
</div>


<style lang='scss'>
    .legend {
        max-width: 350px;
        margin: 0 0 1rem;
        font-family: $sans-serif;

        &__title {
            font-size: 1rem;
            font-weight: 500;
            // text-align: center;
            margin-bottom: 0.4rem;
            color: $gray11;
        }

        &__bar {
            display: flex;
            width: 100%;
        }

        &__cell {
            position: relative;
            flex: 1 1 0;
        }

        &__swatch {
            display: block;
            height: 8px;
        }

        &__cell:first-child &__swatch {
            border-radius: 4px 0 0 4px;
        }

        &__cell:last-child &__swatch {
            border-radius: 0 4px 4px 0;
        }

        // Tick numbers sit on the boundary between adjacent swatches.
        &__edge {
            position: absolute;
            right: 0;
            top: calc(100% + 3px);
            transform: translateX(50%);
            font-size: 0.75rem;
            color: $gray9;
            white-space: nowrap;

            // The leading "0" sits flush to the left edge of the first swatch
            // (no centering transform, so it can't overflow off the legend).
            &--start {
                right: auto;
                left: 0;
                transform: none;
            }

            // The trailing "100" sits flush to the right edge of the last
            // swatch so it can't get clipped off the legend on narrow screens.
            &--end {
                right: 0;
                transform: none;
            }
        }

        &__ends {
            display: flex;
            justify-content: space-between;
            margin-top: 1.4rem;
            font-size: 0.75rem;
            color: $gray9;
        }
    }

    .map-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1rem;

        // Stack into a single column on mobile.
        @media #{$mobile} {
            grid-template-columns: 1fr;
        }

        .map-flex-child {
            position: relative;
        }

        h3 {
            
            font-size: 1rem;
            font-weight: 500;
            // text-align: center;
            margin: 0;
            padding: 12px;
            color: $gray11;
            position: absolute;
            top: 0px;
            left: 0px;
            z-index: 2;
            text-shadow: #fafafa 2px 0px 0px, #fafafa 1.75517px 0.958851px 0px, #fafafa 1.0806px 1.68294px 0px, #fafafa 0.141474px 1.99499px 0px, #fafafa -0.832294px 1.81859px 0px, #fafafa -1.60229px 1.19694px 0px, #fafafa -1.97998px 0.28224px 0px, #fafafa -1.87291px -0.701566px 0px, #fafafa -1.30729px -1.5136px 0px, #fafafa -0.421592px -1.95506px 0px, #fafafa 0.567324px -1.91785px 0px, #fafafa 1.41734px -1.41108px 0px, #fafafa 1.92034px -0.558831px 0px;
        }

        .map {
            aspect-ratio: 1 / 1.25;
        }
    }
</style>
